import { useMemo, useState } from "react";
import {
  Users,
  Building2,
  Phone,
  Mail,
  Percent,
  Plus,
  Search,
  ShieldCheck,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronUp,
  IndianRupee,
  Loader2,
} from "lucide-react";
import { useCanAccess, useNotify, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatPriceInINR } from "@/lib/formatters";
import { useRealEstateContext } from "../providers/RealEstateContext";
import type { ChannelPartner, CpCommissionRow } from "@/types/realEstate";

import { ActionPopupModal } from "../misc/ActionPopupModal";

function ledgerSummary(rows: CpCommissionRow[]) {
  const totalAgreement = rows.reduce((sum, r) => sum + r.agreementValue, 0);
  const totalCommission = rows.reduce((sum, r) => sum + r.cpAmount, 0);
  const pendingPayout = rows
    .filter((r) => r.payoutStatus === "pending")
    .reduce((sum, r) => sum + r.cpAmount, 0);
  return {
    count: rows.length,
    totalAgreement,
    totalCommission,
    pendingPayout,
  };
}

const emptyForm = {
  agency_name: "",
  contact_person: "",
  phone: "",
  rera_number: "",
  commission_percentage: "2.0",
};

const PartnerCommissionLedger = ({ rows }: { rows: CpCommissionRow[] }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { markCommissionPaid } = useRealEstateContext();
  const { canAccess: canMarkPaid } = useCanAccess({
    resource: "sales",
    action: "edit",
  });
  const [expanded, setExpanded] = useState(false);
  const [payingBookingId, setPayingBookingId] = useState<string | null>(null);

  const summary = useMemo(() => ledgerSummary(rows), [rows]);

  const handleMarkPaid = async (bookingId: string) => {
    if (!canMarkPaid) {
      notify("resources.channel_partners.commission_ledger.agents_only", {
        type: "warning",
      });
      return;
    }
    setPayingBookingId(bookingId);
    try {
      const result = await markCommissionPaid(bookingId, "cp");
      if (result.ok) {
        notify(
          "resources.channel_partners.commission_ledger.mark_paid_success",
          {
            type: "info",
          },
        );
      } else {
        notify(
          result.error ||
            "resources.channel_partners.commission_ledger.mark_paid_failed",
          { type: "warning" },
        );
      }
    } finally {
      setPayingBookingId(null);
    }
  };

  if (rows.length === 0) {
    return (
      <p className="text-[11px] text-muted-foreground pt-2 border-t border-dashed">
        {translate("resources.channel_partners.commission_ledger.empty")}
      </p>
    );
  }

  return (
    <div className="pt-2 border-t border-dashed space-y-2">
      <button
        type="button"
        className="w-full text-left space-y-1"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] font-semibold text-foreground flex items-center gap-1">
            <IndianRupee className="w-3 h-3 text-primary" />
            {translate("resources.channel_partners.commission_ledger.title")}
          </span>
          {expanded ? (
            <ChevronUp className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          )}
        </div>
        <p className="text-[10px] text-muted-foreground leading-relaxed">
          {translate("resources.channel_partners.commission_ledger.summary", {
            count: summary.count,
            agreement: formatPriceInINR(summary.totalAgreement),
            commission: formatPriceInINR(summary.totalCommission),
            pending: formatPriceInINR(summary.pendingPayout),
          })}
        </p>
      </button>

      {expanded && (
        <div className="space-y-1.5 max-h-48 overflow-y-auto">
          {rows.map((row) => (
            <div
              key={row.bookingId}
              className="rounded-md border bg-background/80 p-2 space-y-1"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[10px] font-medium text-foreground truncate">
                    {row.unitNumber ||
                      translate(
                        "resources.channel_partners.commission_ledger.unit_unknown",
                      )}
                    {row.buyerName ? ` · ${row.buyerName}` : ""}
                  </p>
                  {row.bookingDate && (
                    <p className="text-[10px] text-muted-foreground">
                      {new Date(row.bookingDate).toLocaleDateString("en-IN")}
                    </p>
                  )}
                </div>
                <Badge
                  className={
                    row.payoutStatus === "paid"
                      ? "bg-emerald-600 text-white text-[9px] shrink-0"
                      : "bg-amber-500 text-white text-[9px] shrink-0"
                  }
                >
                  {translate(
                    row.payoutStatus === "paid"
                      ? "resources.channel_partners.commission_ledger.payout_paid"
                      : "resources.channel_partners.commission_ledger.payout_pending",
                  )}
                </Badge>
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground">
                <span>
                  {translate(
                    "resources.channel_partners.commission_ledger.agreement_value",
                  )}
                  :{" "}
                  <span className="text-foreground font-medium">
                    {formatPriceInINR(row.agreementValue)}
                  </span>
                </span>
                <span>
                  {row.cpPercentage}% ·{" "}
                  <span className="text-foreground font-medium">
                    {formatPriceInINR(row.cpAmount)}
                  </span>
                </span>
              </div>
              {row.payoutStatus === "pending" && canMarkPaid && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 text-[10px] px-2"
                  disabled={payingBookingId === row.bookingId}
                  onClick={() => handleMarkPaid(row.bookingId)}
                >
                  {payingBookingId === row.bookingId ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      {translate(
                        "resources.channel_partners.commission_ledger.marking_paid",
                      )}
                    </>
                  ) : (
                    translate(
                      "resources.channel_partners.commission_ledger.mark_paid",
                    )
                  )}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const ChannelPartnerList = () => {
  const {
    channelPartners,
    addChannelPartner,
    updateChannelPartner,
    deleteChannelPartner,
    channelPartnerLeadStats,
    cpCommissionLedger,
  } = useRealEstateContext();
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [popupInfo, setPopupInfo] = useState<{
    title: string;
    desc: string;
  } | null>(null);

  const ledgerByPartner = useMemo(() => {
    const map = new Map<string, CpCommissionRow[]>();
    for (const row of cpCommissionLedger) {
      const key = String(row.channelPartnerId);
      const list = map.get(key) ?? [];
      list.push(row);
      map.set(key, list);
    }
    return map;
  }, [cpCommissionLedger]);

  const partnersList = channelPartners;

  const filteredPartners = partnersList.filter(
    (cp) =>
      cp.agency_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cp.contact_person.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cp.phone.includes(searchQuery),
  );

  const statsFor = (cpId: string) =>
    channelPartnerLeadStats.find((s) => s.partnerId === String(cpId));

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setIsModalOpen(true);
  };

  const openEdit = (cp: ChannelPartner) => {
    setEditingId(String(cp.id));
    setForm({
      agency_name: cp.agency_name || "",
      contact_person: cp.contact_person || "",
      phone: cp.phone || "",
      rera_number: cp.rera_number || "",
      commission_percentage: String(cp.commission_percentage ?? 2),
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.agency_name.trim() || !form.phone.trim()) return;
    setBusy(true);
    try {
      const payload = {
        agency_name: form.agency_name.trim(),
        contact_person: form.contact_person.trim() || "Agent",
        phone: form.phone.trim(),
        rera_number: form.rera_number.trim() || undefined,
        commission_percentage: Number(form.commission_percentage) || 2.0,
      };
      if (editingId) {
        const updated = await updateChannelPartner(editingId, payload);
        setPopupInfo({
          title: updated ? "Partner Updated" : "Update failed",
          desc: updated
            ? `${updated.agency_name} saved.`
            : "Could not update channel partner.",
        });
      } else {
        await addChannelPartner(payload);
        setPopupInfo({
          title: "Channel Partner Registered",
          desc: `Agency ${payload.agency_name} added to broker network.`,
        });
      }
      setIsModalOpen(false);
      setEditingId(null);
      setForm(emptyForm);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (cp: ChannelPartner) => {
    if (
      !window.confirm(
        `Delete channel partner "${cp.agency_name}"? This cannot be undone.`,
      )
    ) {
      return;
    }
    const ok = await deleteChannelPartner(String(cp.id));
    setPopupInfo({
      title: ok ? "Partner Removed" : "Delete failed",
      desc: ok
        ? `${cp.agency_name} was removed from the network.`
        : "Could not delete — it may still be linked to contacts/bookings.",
    });
  };

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b pb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <Users className="w-7 h-7 text-primary" />
            Channel Partners (Brokers Network)
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage real estate brokers, RERA registrations, lead attributions,
            and commission structures.
          </p>
        </div>

        <Button
          onClick={openCreate}
          className="bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          <Plus className="w-4 h-4 mr-2" /> Register Channel Partner
        </Button>
      </div>

      <div className="bg-card p-4 rounded-xl border shadow-sm flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
          <Input
            placeholder="Search Agency Name, Contact Person, Phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPartners.length === 0 ? (
          <div className="col-span-full rounded-xl border border-dashed bg-muted/20 px-6 py-16 text-center space-y-3">
            <Users className="w-10 h-10 mx-auto text-muted-foreground" />
            <p className="text-lg font-semibold text-foreground">
              No channel partners yet
            </p>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Register brokers to attribute leads and apply 30-day lock rules.
            </p>
            <Button
              onClick={openCreate}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              <Plus className="w-4 h-4 mr-2" /> Register Channel Partner
            </Button>
          </div>
        ) : (
          filteredPartners.map((cp) => (
            <Card
              key={cp.id}
              className="hover:shadow-md transition-shadow border-muted"
            >
              <CardHeader className="pb-3 flex flex-row items-start justify-between space-y-0">
                <div>
                  <CardTitle className="text-lg font-bold flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-primary" />
                    {cp.agency_name}
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1 font-medium">
                    Contact: {cp.contact_person}
                  </p>
                </div>
                <Badge className="bg-indigo-600 text-white font-semibold">
                  <Percent className="w-3 h-3 mr-0.5" />{" "}
                  {cp.commission_percentage}% Comm.
                </Badge>
              </CardHeader>

              <CardContent className="space-y-2 text-xs">
                <p className="text-[11px] text-muted-foreground">
                  Tiered slab auto-updates from attributed bookings: 0–3 → 2%,
                  4+ → 3%.
                </p>
                <div className="bg-muted/50 p-3 rounded-lg space-y-2">
                  <div className="flex items-center text-muted-foreground">
                    <Phone className="w-3.5 h-3.5 mr-2 text-primary" />
                    <span className="text-foreground font-medium">
                      {cp.phone}
                    </span>
                  </div>
                  {cp.email && (
                    <div className="flex items-center text-muted-foreground">
                      <Mail className="w-3.5 h-3.5 mr-2 text-primary" />
                      <span className="text-foreground">{cp.email}</span>
                    </div>
                  )}
                  {cp.rera_number ? (
                    <div className="flex items-center text-emerald-700 dark:text-emerald-400 font-medium pt-1">
                      <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />
                      RERA: {cp.rera_number}
                    </div>
                  ) : (
                    <div className="flex items-center text-muted-foreground pt-1">
                      <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />
                      RERA: Not provided
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between gap-2 pt-1">
                  <div className="flex flex-wrap gap-1.5">
                    <Badge variant="secondary" className="text-[10px]">
                      {statsFor(String(cp.id))?.leadCount ?? 0} linked lead
                      {(statsFor(String(cp.id))?.leadCount ?? 0) === 1
                        ? ""
                        : "s"}
                    </Badge>
                    {(statsFor(String(cp.id))?.lockedLeadCount ?? 0) > 0 && (
                      <Badge className="bg-amber-600 text-white text-[10px]">
                        {statsFor(String(cp.id))!.lockedLeadCount} locked
                        {statsFor(String(cp.id))!.nextLockExpiry
                          ? ` · to ${new Date(statsFor(String(cp.id))!.nextLockExpiry!).toLocaleDateString("en-IN")}`
                          : ""}
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2"
                      onClick={() => openEdit(cp)}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive"
                      onClick={() => handleDelete(cp)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <PartnerCommissionLedger
                  rows={ledgerByPartner.get(String(cp.id)) ?? []}
                />
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              {editingId
                ? "Edit Channel Partner"
                : "Register New Channel Partner"}
            </DialogTitle>
            <DialogDescription>
              {editingId
                ? "Update broker details and commission."
                : "Add a real estate broker or agency to your partner network."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-3 text-sm">
            <div className="space-y-1">
              <label className="text-xs font-semibold">Agency Name</label>
              <Input
                placeholder="e.g. Acme Realty Pvt Ltd"
                value={form.agency_name}
                onChange={(e) =>
                  setForm({ ...form, agency_name: e.target.value })
                }
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold">Contact Person</label>
              <Input
                placeholder="e.g. Ramesh Chandra"
                value={form.contact_person}
                onChange={(e) =>
                  setForm({ ...form, contact_person: e.target.value })
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-semibold">Phone Number</label>
                <Input
                  placeholder="+91 98765 43210"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold">Commission %</label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="2.0"
                  value={form.commission_percentage}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      commission_percentage: e.target.value,
                    })
                  }
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold">
                RERA Registration Number{" "}
                <span className="font-normal text-muted-foreground">
                  (optional)
                </span>
              </label>
              <Input
                placeholder="e.g. A51800009999"
                value={form.rera_number}
                onChange={(e) =>
                  setForm({ ...form, rera_number: e.target.value })
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
              disabled={busy || !form.agency_name.trim() || !form.phone.trim()}
              onClick={handleSave}
            >
              {busy
                ? "Saving…"
                : editingId
                  ? "Save Changes"
                  : "Register Partner"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ActionPopupModal
        isOpen={!!popupInfo}
        onClose={() => setPopupInfo(null)}
        title={popupInfo?.title || ""}
        description={popupInfo?.desc || ""}
      />
    </div>
  );
};

export default ChannelPartnerList;
